import React from "react";
import { Box, Card, IconButton, Typography } from "@material-ui/core";
import clsx from "clsx";
import moment from "moment";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import ScheduleRoundedIcon from "@material-ui/icons/ScheduleRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import EventBusyRoundedIcon from "@material-ui/icons/EventBusyRounded";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import WifiRoundedIcon from "@material-ui/icons/WifiRounded";
import BusinessRoundedIcon from "@material-ui/icons/BusinessRounded";
import { useSidebarStyles, SIDEBAR_COLORS } from "./styles";

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

const getPatientName = (appt) =>
  appt.Patient
    ? `${appt.Patient.firstName} ${appt.Patient.lastName || ""}`.trim()
    : appt.title || "Unknown";

const isOnline = (appt) =>
  (appt.appointmentType || "").toLowerCase() === "online";
const isCancelled = (appt) =>
  (appt.status || "").toUpperCase().startsWith("CANCEL");
const cancelledTooltip = (appt) =>
  [
    "Cancelled",
    appt.cancellationReason ? `— ${appt.cancellationReason}` : "",
    appt.cancelledBy ? `(by ${appt.cancelledBy.toLowerCase()})` : "",
  ]
    .filter(Boolean)
    .join(" ");
const isCompleted = (appt) =>
  (appt.status || "").toUpperCase() === "COMPLETED";
const isPast = (appt) => {
  const t = appt.endTime || appt.startTime;
  return t ? moment(t).isBefore(moment()) : false;
};
const formatClock = (ms) => (ms ? moment(ms).format("hh:mm A") : "");

const SelectedDaySidebar = ({
  appointments = [],
  selectedDate,
  setSelectedDate,
  calMonth,
  setCalMonth,
  onCardClick,
}) => {
  const classes = useSidebarStyles();

  const gridStart = calMonth.clone().startOf("month").startOf("isoWeek");
  const calDays = Array.from({ length: 42 }, (_, i) =>
    gridStart.clone().add(i, "days")
  );
  const onSelectDay = (day) => {
    setSelectedDate(day.clone());
    if (day.month() !== calMonth.month()) {
      setCalMonth(day.clone().startOf("month"));
    }
  };

  // Cancelled appointments get their own section so the slot history stays
  // visible; they are excluded from the other three.
  //  - Upcoming: not yet completed and not past (logic unchanged)
  //  - Completed: the start→end (check-in/check-out) flow finished (status COMPLETED)
  //  - Missing: time passed but the flow was never completed
  const dayAppts = appointments
    .filter((a) => moment(a.startTime).isSame(selectedDate, "day"))
    .sort((a, b) => a.startTime - b.startTime);
  const upcoming = dayAppts.filter(
    (a) => !isCancelled(a) && !isCompleted(a) && !isPast(a)
  );
  const completed = dayAppts.filter(
    (a) => !isCancelled(a) && isCompleted(a)
  );
  const missing = dayAppts.filter(
    (a) => !isCancelled(a) && !isCompleted(a) && isPast(a)
  );
  const cancelled = dayAppts.filter(isCancelled);

  const renderStatus = (appt) =>
    isOnline(appt) ? (
      <span className={clsx(classes.statusChip, classes.onlineChip)}>
        <WifiRoundedIcon className={classes.statusIcon} />
        Online
      </span>
    ) : (
      <span className={clsx(classes.statusChip, classes.offlineChip)}>
        <BusinessRoundedIcon className={classes.statusIcon} />
        Offline
      </span>
    );

  // Cancelled cards are inert: struck through and not clickable, with the
  // reason surfaced on hover.
  const renderApptCard = (appt, accentColor, leadIcon, muted = false) => {
    const cancelled = isCancelled(appt);
    const struck = muted || cancelled;
    return (
      <Box
        key={appt.id}
        title={cancelled ? cancelledTooltip(appt) : undefined}
        className={clsx(classes.apptCard, cancelled && classes.apptCardInert)}
        style={{
          borderLeftColor: accentColor,
          ...(struck ? { opacity: 0.7 } : {}),
        }}
        onClick={() => {
          if (cancelled) return;
          if (onCardClick) onCardClick(appt);
        }}
      >
        <Box className={classes.apptCardLeft}>
          {leadIcon}
          <Typography
            className={classes.apptCardText}
            style={struck ? { textDecoration: "line-through" } : undefined}
          >
            {formatClock(appt.startTime)} - {getPatientName(appt)}
          </Typography>
        </Box>
        {renderStatus(appt)}
      </Box>
    );
  };

  const renderSectionHeader = (label, color, count) => (
    <Box className={classes.sectionHeader}>
      <span className={classes.sectionDot} style={{ backgroundColor: color }} />
      <Typography className={classes.sectionLabel} style={{ color }}>
        {label} ({count})
      </Typography>
    </Box>
  );

  return (
    <Card className={classes.sidebarCard} elevation={0}>
      <Box className={classes.sidebarHeader}>
        <CalendarTodayIcon className={classes.headerIcon} fontSize="small" />
        <Typography className={classes.sidebarTitle}>
          Selected Day: {selectedDate.format("ddd, MMM DD")}
        </Typography>
      </Box>

      <Box className={classes.miniCal}>
        <Box className={classes.miniCalHeader}>
          <IconButton
            className={classes.miniCalNav}
            size="small"
            onClick={() => setCalMonth((m) => m.clone().subtract(1, "month"))}
          >
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton>
          <Typography className={classes.miniCalMonth}>
            {calMonth.format("MMMM YYYY")}
          </Typography>
          <IconButton
            className={classes.miniCalNav}
            size="small"
            onClick={() => setCalMonth((m) => m.clone().add(1, "month"))}
          >
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.miniCalGrid}>
          {WEEKDAY_LABELS.map((w, i) => (
            <Box key={`wd-${i}`} className={classes.miniCalWeekday}>
              {w}
            </Box>
          ))}
          {calDays.map((day) => {
            const muted = day.month() !== calMonth.month();
            const isToday = day.isSame(moment(), "day");
            const isSelected = day.isSame(selectedDate, "day");
            return (
              <Box
                key={`d-${day.format("YYYY-MM-DD")}`}
                className={clsx(
                  classes.miniCalDay,
                  muted && classes.miniCalDayMuted,
                  isToday && classes.miniCalDayToday,
                  isSelected && classes.miniCalDaySelected
                )}
                onClick={() => onSelectDay(day)}
              >
                {day.date()}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box className={classes.section}>
        {renderSectionHeader("Upcoming", SIDEBAR_COLORS.UPCOMING, upcoming.length)}
        {upcoming.length === 0 ? (
          <Typography className={classes.emptySection}>
            No upcoming appointments
          </Typography>
        ) : (
          upcoming.map((appt) =>
            renderApptCard(
              appt,
              SIDEBAR_COLORS.UPCOMING,
              <ScheduleRoundedIcon
                className={classes.statusIcon}
                style={{ color: SIDEBAR_COLORS.UPCOMING }}
              />
            )
          )
        )}
      </Box>

      <Box className={classes.section}>
        {renderSectionHeader(
          "Completed",
          SIDEBAR_COLORS.COMPLETED,
          completed.length
        )}
        {completed.length === 0 ? (
          <Typography className={classes.emptySection}>
            No completed visits
          </Typography>
        ) : (
          completed.map((appt) =>
            renderApptCard(
              appt,
              SIDEBAR_COLORS.COMPLETED,
              <CheckCircleRoundedIcon
                className={classes.statusIcon}
                style={{ color: SIDEBAR_COLORS.COMPLETED }}
              />
            )
          )
        )}
      </Box>

      <Box className={classes.section}>
        {renderSectionHeader("Missing", SIDEBAR_COLORS.MISSING, missing.length)}
        {missing.length === 0 ? (
          <Typography className={classes.emptySection}>
            No missed appointments
          </Typography>
        ) : (
          missing.map((appt) =>
            renderApptCard(
              appt,
              SIDEBAR_COLORS.MISSING,
              <EventBusyRoundedIcon
                className={classes.statusIcon}
                style={{ color: SIDEBAR_COLORS.MISSING }}
              />,
              true
            )
          )
        )}
      </Box>

      {cancelled.length > 0 && (
        <Box className={classes.section}>
          {renderSectionHeader(
            "Cancelled",
            SIDEBAR_COLORS.CANCELLED,
            cancelled.length
          )}
          {cancelled.map((appt) =>
            renderApptCard(
              appt,
              SIDEBAR_COLORS.CANCELLED,
              <CancelRoundedIcon
                className={classes.statusIcon}
                style={{ color: SIDEBAR_COLORS.CANCELLED }}
              />
            )
          )}
        </Box>
      )}
    </Card>
  );
};

export default SelectedDaySidebar;
